import React from 'react';
import { Link } from 'react-router-dom';
import { Icon } from '../primitives/Icon';
import { UserMenu } from './UserMenu';

export interface Breadcrumb {
  label: string;
  to?: string;
}

interface TopBarProps {
  breadcrumbs?: Breadcrumb[];
  actions?: React.ReactNode;
  hasNotifications?: boolean;
  /** Unread notification count for the bell badge. When >0 a numeric badge is
   *  shown (capped at "99+"); falls back to a dot via hasNotifications. */
  unreadCount?: number;
  onNotificationsClick?: () => void;
  /** Optional secondary icon — internal changelog "what's new" drawer.
   *  Shown next to the bell. The badge fires when the user has new
   *  changelog posts they haven't opened yet. */
  unreadChangelogCount?: number;
  onChangelogClick?: () => void;
  onMenuClick?: () => void;
  onSearchClick?: () => void;
  /** Rendered at the start of the action row — the quick-create menu. */
  quickCreate?: React.ReactNode;
}

export const TopBar: React.FC<TopBarProps> = ({
  breadcrumbs = [],
  actions,
  hasNotifications,
  unreadCount,
  onNotificationsClick,
  unreadChangelogCount,
  onChangelogClick,
  onMenuClick,
  onSearchClick,
  quickCreate,
}) => {
  return (
    <div className="sticky top-0 z-20 bg-white/80 dark:bg-slate-950/80 backdrop-blur-md border-b border-slate-200 dark:border-slate-800">
      <div className="px-4 sm:px-6 lg:px-8 h-16 flex items-center gap-3">
        {/* Hamburger menu (mobile only) */}
        <button
          onClick={onMenuClick}
          className="lg:hidden size-10 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-900 flex items-center justify-center text-slate-600 flex-shrink-0"
          aria-label="פתח תפריט"
        >
          <Icon name="menu" size={22} />
        </button>

        {/* Breadcrumbs — hidden when search bar takes the space on narrower desktops */}
        <nav className="hidden xl:flex items-center gap-2 text-sm min-w-0 flex-shrink-0" aria-label="Breadcrumb">
          {breadcrumbs.map((crumb, i) => {
            const isLast = i === breadcrumbs.length - 1;
            return (
              <React.Fragment key={i}>
                {i > 0 && <Icon name="chevron_right" size={14} className="text-slate-300 hidden rtl:inline" />}
                {i > 0 && <Icon name="chevron_left" size={14} className="text-slate-300 rtl:hidden" />}
                {isLast || !crumb.to ? (
                  <span className={isLast ? 'font-semibold text-slate-900 dark:text-white truncate' : 'text-slate-500'}>
                    {crumb.label}
                  </span>
                ) : (
                  <Link to={crumb.to} className="text-slate-500 hover:text-slate-900 dark:hover:text-white transition-colors truncate">
                    {crumb.label}
                  </Link>
                )}
              </React.Fragment>
            );
          })}
        </nav>

        {/* Search — full-width bar on desktop, icon on mobile */}
        <div className="flex-1 flex items-center justify-center min-w-0">
          <button
            onClick={onSearchClick}
            className="hidden sm:flex w-full max-w-md h-10 items-center gap-2 ps-3 pe-2 rounded-lg bg-slate-100 dark:bg-slate-900 border-0 text-sm text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-800 transition-colors"
          >
            <Icon name="search" size={16} />
            <span className="flex-1 text-start">חיפוש מהיר...</span>
            <kbd className="hidden md:inline text-[10px] font-mono bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-700 rounded px-1.5 py-0.5 text-slate-500">
              ⌘K
            </kbd>
          </button>
        </div>

        <div className="flex items-center gap-1 flex-shrink-0">
          {quickCreate}

          {/* Search icon — only on mobile (small) */}
          <button
            onClick={onSearchClick}
            className="sm:hidden size-10 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-900 flex items-center justify-center text-slate-600"
            aria-label="חיפוש"
          >
            <Icon name="search" size={20} />
          </button>

          {/* What's-new / changelog icon — placed before notifications
              because changes-to-the-system feel more "announce-y" than
              the personal action items in the bell, and people scan
              right-to-left in RTL so the leftmost icon is the most
              interrupting one. */}
          {onChangelogClick && (
            <button
              onClick={onChangelogClick}
              className="size-10 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-900 flex items-center justify-center relative transition-colors"
              aria-label="מה חדש במערכת"
              title="מה חדש במערכת"
            >
              <Icon name="campaign" size={20} className="text-slate-600 dark:text-slate-400" />
              {typeof unreadChangelogCount === 'number' && unreadChangelogCount > 0 && (
                <span className="absolute -top-0.5 -end-0.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center text-[10px] font-bold leading-none rounded-full bg-emerald-500 text-white ring-2 ring-white dark:ring-slate-950">
                  {unreadChangelogCount > 99 ? '99+' : unreadChangelogCount}
                </span>
              )}
            </button>
          )}

          <button
            onClick={onNotificationsClick}
            className="size-10 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-900 flex items-center justify-center relative transition-colors"
            aria-label="התראות"
          >
            <Icon name="notifications" size={20} className="text-slate-600 dark:text-slate-400" />
            {typeof unreadCount === 'number' && unreadCount > 0 ? (
              <span className="absolute -top-0.5 -end-0.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center text-[10px] font-bold leading-none rounded-full bg-accent text-white ring-2 ring-white dark:ring-slate-950">
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            ) : hasNotifications ? (
              <span className="absolute top-2 end-2 size-2 rounded-full bg-accent ring-2 ring-white dark:ring-slate-950" />
            ) : null}
          </button>

          {actions}

          <div className="ms-1 ps-1 border-s border-slate-200 dark:border-slate-800">
            <UserMenu />
          </div>
        </div>
      </div>
    </div>
  );
};
